import { Table } from 'rsuite';
import { useTranslation } from 'react-i18next';
import LockIcon from '@material-ui/icons/Lock';
import { DaysOfWeekNames, ShiftDisplayType } from '../../../../Enums';
import { getEmployeeSubmissionStatusLabel } from '../../../../Enums/Helpers/LabellingHelper';
import { getShiftsForDay, getPatternRowNumber } from '../../../../Employee/components/ShiftLineSelectionTable/ShiftLineSelectionTable';
import './BiddingResultsTable.scss';

const { Column, HeaderCell, Cell } = Table;

/**
 * @typedef {'asc'|'desc'} SortType
 * @typedef {Object} ReportTableProps
 * @property {string} sortColumn
 * @property {Function} setSortColumn
 * @property {SortType} sortType
 * @property {Function} setSortType
 * @property {ManagerAppData.IDataviewFilter[]} dataviewFields
 * @property {ManagerAppData.IEmployee[]} dataviewEmployeeData
 * @property {DayOfWeek} anchorDay
 * @property {boolean} showAwardedBid
 * @property {BidProfile.ISchedulePatternsShiftTemplateDictionary} shiftsDictionary
 * @property {number} dayCount
 * @property {boolean} isPeriodTypeDay
 * @property {number} daysPerPeriod
 * @property {string} shiftDisplayType
 *
 * @param { ReportTableProps & import('rsuite').TableProps } input
 */
function BiddingResultsTable({
    sortColumn, setSortColumn, sortType, setSortType, dataviewFields = [], dataviewEmployeeData, anchorDay = 0,
    shiftsDictionary, showAwardedBid, dayCount, isPeriodTypeDay = false, daysPerPeriod = 7, shiftDisplayType = ShiftDisplayType.ShiftLabel, ...rest
}) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'common.reports.biddingResults.columns' });
    /**
     * @param {string} newSortColumn
     * @param {string} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    /**
     * @param {string} col
     * @param {number} eId
     * @returns {string}
     */
    const getDataviewAttributeValue = (col, eId) => {
        const ee = dataviewEmployeeData.find((e) => e.employeeId === eId);
        const val = ee?.dataviewAttributes.find((x) => x.key === col);
        return val?.value || '';
    };

    return (
        <>
            <Table
                key="employee-bidding-results-brt"
                className="brt-detailed-selection"
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                {...rest}
            >
                <Column
                    key="name"
                    sortable
                    resizable
                    width={150}
                >
                    <HeaderCell>{t('name')}</HeaderCell>
                    <Cell dataKey="name" />
                </Column>
                <Column
                    key="rank"
                    sortable
                    resizable
                    width={75}
                >
                    <HeaderCell>{t('rank')}</HeaderCell>
                    <Cell dataKey="rank">
                        {
                            (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) => (rowData.isLocked
                                ? (
                                    <div className="brt-lock-icon">
                                        <LockIcon />
                                    </div>
                                )
                                : rowData.rank)
                        }
                    </Cell>
                </Column>
                <Column
                    key="status"
                    sortable
                    resizable
                    width={150}
                >
                    <HeaderCell>{t('status')}</HeaderCell>
                    <Cell dataKey="status">
                        {
                            (/** @type { BidProfileManagement.EmployeeSelectionsTableRowData } */ rowData) =>
                                getEmployeeSubmissionStatusLabel(rowData.status)
                        }
                    </Cell>
                </Column>
                <Column
                    key="scheduledOn"
                    sortable
                    resizable
                    width={260}
                >
                    <HeaderCell>{t('scheduledOn')}</HeaderCell>
                    <Cell dataKey="scheduledOn" />
                </Column>
                {
                    dataviewFields
                        .filter((x) => x.checked)
                        .map((element) => (
                            <Column
                                key={element.key}
                                resizable
                                width={150}
                                sortable={false}
                            >
                                <HeaderCell>{element.label}</HeaderCell>
                                <Cell>
                                    {
                                        (
                                            /** @type {EmployeeBiddingResultsReport.IEmployeeBidResult} */rowData,
                                        ) => (
                                            <span>
                                                {getDataviewAttributeValue(element.key, rowData.employeeId)}
                                            </span>
                                        )
                                    }
                                </Cell>
                            </Column>
                        ))
                }
                {
                    showAwardedBid && (
                        <Column
                            key="ScheduleName"
                            sortable={false}
                            resizable
                            width={110}
                        >
                            <HeaderCell>{t('scheduleName')}</HeaderCell>
                            <Cell>
                                {
                                    (
                                        /** @type {EmployeeBiddingResultsReport.IEmployeeBidResult} */rowData,
                                    ) => (rowData.groupedAwardedItem
                                        ? (rowData.groupedAwardedItem?.schedulePatternTemplate.name)
                                        : null)
                                }
                            </Cell>
                        </Column>
                    )
                }
                {
                    showAwardedBid && (
                        <Column
                            key="RowNumber"
                            sortable={false}
                            resizable
                            width={87}
                        >
                            <HeaderCell>{t('week')}</HeaderCell>
                            <Cell>
                                {
                                    (
                                        /** @type {EmployeeBiddingResultsReport.IEmployeeBidResult} */rowData,
                                    ) => (rowData.groupedAwardedItem
                                        ? getPatternRowNumber(rowData.groupedAwardedItem)
                                        : null)
                                }
                            </Cell>
                        </Column>
                    )
                }
                {
                    showAwardedBid && (
                        Array.from(Array(7).keys()).map((x) => (
                            <Column
                                key={`day_${x}`}
                                width={150}
                            >
                                <HeaderCell>
                                    {isPeriodTypeDay ? `${x + 1}` : DaysOfWeekNames[(x + anchorDay) % 7]}
                                </HeaderCell>
                                <Cell>
                                    {
                                        /**
                                        * @param {EmployeeBiddingResultsReport.IEmployeeBidResult} rowData
                                        */
                                        (rowData) => (rowData.groupedAwardedItem
                                            ? getShiftsForDay(
                                                x,
                                                shiftsDictionary,
                                                rowData.groupedAwardedItem,
                                                dayCount,
                                                daysPerPeriod,
                                                false,
                                                shiftDisplayType,
                                            )
                                            : null)
                                    }
                                </Cell>
                            </Column>
                        ))
                    )
                }

            </Table>
        </>
    );
}

export default BiddingResultsTable;
